// WithdrawForm — withdraw deposited R₿L (backend sends from treasury).
// 5% fee warning when any active deposit is younger than 30 days.
import React, { useState } from "react";
import { Loader2, AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import { API } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { tVault } from "../../i18n/vault";

const fmt = (n) => Number(n).toLocaleString();

export default function WithdrawForm({ me, onWithdrawn }) {
  const { lang } = useLanguage();
  const t = (k) => tVault(lang, k);
  const { token } = useAuth();
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const total = me?.total_deposited_rbl || 0;
  if (!me || total <= 0) {
    return <p className="text-xs text-white/40" data-testid="vault-no-deposit">{t("noDeposit")}</p>;
  }
  const hasEarly = (me.deposits || []).some((d) => !d.is_eligible);
  const wallet = me.deposits[me.deposits.length - 1]?.wallet_address;

  const withdraw = async (all = false) => {
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await fetch(`${API}/vault/withdraw`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          wallet_address: wallet,
          amount_rbl: all ? null : Math.floor(Number(amount)),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail?.error || "withdraw_failed");
      setResult(d);
      setAmount("");
      onWithdrawn?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="rounded-xl border border-emerald-400/40 bg-emerald-400/10 p-4 text-center" data-testid="vault-withdraw-success">
        <CheckCircle2 className="mx-auto h-7 w-7 text-emerald-400" />
        <p className="mt-2 text-sm font-black text-white">{t("withdrawSuccess")}</p>
        <p className="mt-1 font-mono text-sm text-white/70">
          {t("net")}: <b className="text-emerald-300">{fmt(result.net_rbl)} R₿L</b>
          {result.fee_rbl > 0 && <span className="text-white/40"> · {t("fee")}: {fmt(result.fee_rbl)} R₿L</span>}
        </p>
        <a href={`https://basescan.org/tx/${result.tx_hash}`} target="_blank" rel="noopener noreferrer"
          className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-300 underline">
          <ExternalLink className="h-3 w-3" /> {t("viewTx")}
        </a>
      </div>
    );
  }

  return (
    <div data-testid="vault-withdraw-form">
      <p className="text-[11px] text-white/55">
        {t("available")}: <b className="font-mono text-white" data-testid="vault-withdraw-available">{fmt(total)} R₿L</b>
      </p>
      {hasEarly && (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-[11px] text-amber-200" data-testid="vault-early-warning">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t("earlyWithdrawWarning")}
        </p>
      )}
      <input
        type="number" min="0" step="1" inputMode="numeric" placeholder={t("amount")}
        value={amount} onChange={(e) => setAmount(e.target.value)}
        data-testid="vault-withdraw-amount"
        className="mt-3 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 font-mono text-lg font-bold text-white outline-none placeholder:text-white/25 focus:border-amber-400/60"
      />
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          onClick={() => withdraw(false)}
          disabled={busy || !amount || Number(amount) <= 0 || Number(amount) > total}
          data-testid="vault-withdraw-btn"
          className="rounded-xl border border-white/20 py-3 text-xs font-black uppercase tracking-[0.16em] text-white transition-all hover:border-amber-400/60 disabled:opacity-40"
        >
          {busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : t("withdraw")}
        </button>
        <button
          onClick={() => withdraw(true)}
          disabled={busy}
          data-testid="vault-withdraw-all-btn"
          className="rounded-xl bg-white/10 py-3 text-xs font-black uppercase tracking-[0.16em] text-white transition-all hover:bg-white/20 disabled:opacity-40"
        >
          {busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : t("withdrawAll")}
        </button>
      </div>
      {error && (
        <p className="mt-2 rounded-lg border border-red-400/30 bg-red-400/[0.06] px-3 py-2 text-[11px] text-red-300" data-testid="vault-withdraw-error">
          {t("error")}: {error}
        </p>
      )}
    </div>
  );
}
